import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';

import { useCreateCapitalCallMutation } from 'api/main';
import { capitalCallSchemas } from 'schemas';
import { EURO_SYMBOL, billStatus as closingStatus } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { CustomButton, CustomDatePicker, CustomTextField } from 'components';
import { Box, FormHelperText, InputAdornment } from '@mui/material';

const AddCapitalCall = props => {
    const { open, handleClose, totalInvested, selectedFundId, totalCalledPercentage, closings } =
        props;

    const [createCapitalCall, { isLoading: buttonLoader, isSuccess }] =
        useCreateCapitalCallMutation();

    const [newCapitalCall, setNewCapitalCall] = useState({
        date: moment().add(1, 'd'),
        percentage: '',
        reason: '',
    });
    const [percentageError, setPercentageError] = useState('');
    const issueSum = useMemo(
        () => (totalInvested * (+newCapitalCall.percentage / 100)).toFixed(2),
        [totalInvested, newCapitalCall]
    );

    const isSubmitEnabled = useMemo(() => {
        const reasonNotEmpty = newCapitalCall.reason?.trim().length > 0;
        return newCapitalCall.percentage > 0 && reasonNotEmpty;
    }, [newCapitalCall]);

    const minDate = useMemo(() => {
        let lastDate = moment().utcOffset(0).add(1, 'd').startOf('day');
        if (selectedFundId) {
            lastDate = closings.reduce(
                (maxDate, c) =>
                    c.investmentFund.id === selectedFundId &&
                    (c.status === closingStatus.COMPLETED || c.status === closingStatus.ACTIVATED)
                        ? maxDate.isAfter(c.issueDate, 'd')
                            ? maxDate
                            : moment(c.issueDate).add(1, 'd').utcOffset(0)
                        : maxDate,
                lastDate
            );
        }
        lastDate.startOf('day');
        return lastDate;
    }, [selectedFundId, closings]);

    const handleDateChange = useCallback(
        newDate => setNewCapitalCall(oldValue => ({ ...oldValue, date: newDate })),
        []
    );

    const handleChange = useCallback(e => {
        const { name, value } = e.target;
        setNewCapitalCall(oldValue => ({ ...oldValue, [name]: value }));
    }, []);

    const handleAddCapitalCall = useCallback(() => {
        if (
            !newCapitalCall.percentage ||
            +newCapitalCall.percentage > 100 - totalCalledPercentage
        ) {
            setPercentageError(texts.FUND_CALL_PERCENTAGE_ERROR(100 - totalCalledPercentage));
            return;
        }
        setPercentageError('');
        createCapitalCall(
            capitalCallSchemas.addCapitalCall({
                ...newCapitalCall,
                date: newCapitalCall.date.utcOffset(0).startOf('day'),
                investmentFundId: selectedFundId,
            })
        );
    }, [newCapitalCall, totalCalledPercentage, createCapitalCall, selectedFundId]);

    useEffect(() => {
        if (!open) {
            setNewCapitalCall({ date: minDate, percentage: '' });
        } else {
            setNewCapitalCall(oldCapitalCall => ({ ...oldCapitalCall, date: minDate }));
        }
    }, [open, minDate]);

    useEffect(() => {
        if (isSuccess) {
            handleClose();
        }
    }, [isSuccess, handleClose]);

    if (!open) return null;

    return (
        <React.Fragment>
            <Box display="flex" justifyContent="space-between" alignItems="center" paddingY={2}>
                <CustomDatePicker
                    sx={{ marginBottom: 0 }}
                    value={newCapitalCall.date}
                    minDate={minDate}
                    onChange={handleDateChange}
                    label={texts.DATE}
                />
                <CustomTextField
                    name="percentage"
                    value={newCapitalCall.percentage}
                    formatNumber
                    error={percentageError}
                    showError={false}
                    onChange={handleChange}
                    label={texts.PERCENTAGE}
                    endAdornment={<InputAdornment position="start">%</InputAdornment>}
                />
                <CustomTextField
                    name="amount"
                    disabled
                    value={issueSum}
                    label={texts.MONTANT_APPELE}
                    formatNumber
                    endAdornment={<InputAdornment position="start">{EURO_SYMBOL}</InputAdornment>}
                />
            </Box>
            <CustomTextField
                name="reason"
                label={texts.FUND_CALLS_REASON}
                multiline
                rows={4}
                value={newCapitalCall.reason}
                onChange={handleChange}
            />
            {percentageError && (
                <FormHelperText error={!!percentageError}>{percentageError}</FormHelperText>
            )}
            <Box textAlign="center">
                <CustomButton
                    variant="contained"
                    loading={buttonLoader}
                    onClick={handleAddCapitalCall}
                    label={texts.SAVE_AND_CONTINUE}
                    disabled={!isSubmitEnabled}
                />
            </Box>
        </React.Fragment>
    );
};

export default AddCapitalCall;
